import React, { FC, Fragment, useState } from 'react';
import { Box, Paper, FormControl, Button, Alert, Link } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { labClientTemplate } from 'apollo/templates/client-directory';
import { savePtjCustomerNumberTemplate } from 'apollo/templates/client-directory';
import { LabClient } from 'models/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { isPtjInternalOrganization } from 'utils/organizations';
import { Organization } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

const PtjSettings: FC = () => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const { id } = useParams<{ id: string }>();
    const [labClient, setLabClient] = useState<LabClient>({} as LabClient);
    const [ptjCustomerNumber, setPtjCustomerNumber] = useState<string>('');
    const [changesSaved, setChangesSaved] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();
    const [isDisabled, setIsDisabled] = useState(false);
    const currentOrg = currentOrgVar();

    const { loading: labClientLoading } = useQuery(labClientTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clientId: id,
        },
        onCompleted: ({ labClient }: { labClient: LabClient }) => {
            setLabClient(labClient);
            if (isPtjInternalOrganization((labClient?.customer as Organization).organizationNumber)) {
                // Is current organization an internal PTJ lab
                if (isPtjInternalOrganization(currentOrg.organizationNumber)) {
                    setPtjCustomerNumber((labClient?.customer as Organization)?.ptjCustomerNumber || '');
                } else {
                    setPtjCustomerNumber('101');
                }
                setIsDisabled(true);
            } else {
                setPtjCustomerNumber(labClient?.ptjCustomerNumber || '');
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [savePtjCustomerNumber, { loading, error }] = useMutation(savePtjCustomerNumberTemplate, {
        onCompleted: ({ savePtjCustomerNumber: updatedClient }) => {
            setLabClient(Object.assign({ ...labClient, ...updatedClient }));
            setChangesSaved(true);
        },
        onError: (err) => {
            console.error(err, 'err');
        },
    });

    const handleClick = () => {
        savePtjCustomerNumber({
            variables: {
                customerId: id,
                ptjCustomerNumber: ptjCustomerNumber,
            },
        });
    };

    const handleBlur = () => {
        if (!ptjCustomerNumber) {
            setErrorMsg(translate('organization:VALIDATION:PTJ_CUSTOMER_NUMBER'));
        } else {
            setErrorMsg('');
        }
    };

    const onPtjCustomerNumberChange = (value: string) => {
        setPtjCustomerNumber(value);
    };

    if (labClientLoading || loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {!(labClient.customer as Organization)?.parentClinicId ? (
                <Fragment>
                    <Box m={2}>
                        <Box mb={2}>
                            <FormControl sx={{ maxWidth: 450 }}>
                                <NumberOnlyTextField
                                    id="ptj-customer-number"
                                    size="small"
                                    label={translate('organization:CUSTOMER_NUMBER')}
                                    onValueChange={onPtjCustomerNumberChange}
                                    value={ptjCustomerNumber}
                                    required
                                    onBlur={handleBlur}
                                    helperText={errorMsg}
                                    error={!!errorMsg}
                                    disabled={isDisabled}
                                />
                            </FormControl>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Button
                                disabled={!ptjCustomerNumber || isDisabled}
                                variant="contained"
                                type="button"
                                onClick={handleClick}
                            >
                                {translate('common:SAVE')}
                            </Button>
                        </Box>
                    </Box>
                </Fragment>
            ) : (
                <Paper variant="outlined">
                    <Box p={2}>
                        <Alert severity="info">
                            {translate('client-directory:DISABLED_DUE_TO_PARENT')}
                            &nbsp;
                            {(labClient.customer as Organization)?.parentClinicId ? (
                                <Link
                                    component={RouterLink}
                                    to={`/client-directory/clients/${(labClient.customer as Organization)?.parentClinicId}/ptj-settings`}
                                >
                                    {translate('common:SHOW_CLINIC')}
                                </Link>
                            ) : undefined}
                        </Alert>
                    </Box>
                </Paper>
            )}
        </Fragment>
    );
};

export default PtjSettings;
